import React, { ReactElement, useEffect, useRef, useState } from 'react'

import DashboardSpeedometerBlack from './SpeedometerDials'
import { hasKey } from '../../../Common/Utils'
import CBullseye from '../../../../NonReact/Dials/Bullseye'
import CProgressRing from '../../../../NonReact/Dials/ProgressRing'
import CGradatedDial from '../../../../NonReact/Dials/GradatedDial'
import CGlideSlope from '../../../../NonReact/Dials/GlideSlope'
import CBarGauge from '../../../../NonReact/Dials/BarGauge'
import CConeGauge from '../../../../NonReact/Dials/ConeGauge'
import { TileDisplayAsValues, TileDisplayedAsGraphDialValues } from '../Types'

// import './tilePreview.scss'

export type TilePreviewProps = {
    backgroundColour: number
    textColour: number
    tileDisplayedAs: TileDisplayAsValues | TileDisplayedAsGraphDialValues
    isGraphDialAxisReversed: boolean
    isGraphDialFacadeReversed: boolean
    graphDialScaleRangeMax: number
    title: string
    displayTitle: boolean
    statID: number
    currentValueNormalised: number
    currentValueText: string
    imageURL?: string
    fitImage?: boolean
    unitType: number
}

export enum TileBackgroundColors {
    BLACK = '#212121',
    WHITE = '#F5F5F5',
    BLANC = '#ffffff',
    SMOKE = '#BDBDBD',
    GREY = '#9E9E9E',
    BATTLESHIP = '#757575',
    AQUA = '#81D4FA',
    VIVID_SKY = '#1cd2fa',
    BLUE = '#03A9F4',
    ELECTRIC_INDIGO = '#4450ff',
    WATERS = '#0277BD',
    SKY = '#0065B5',
    AKIXI = '#005497',
    MIDNIGHT = '#002f54',
    PISTACHIO = '#a5d6a7',
    AQUAMARINE = '#22f4ad',
    GREEN = '#00c853',
    FOREST = '#2e7d32',
    SALMON = '#e57373',
    RED = '#e53935',
    ROSITA = '#c62828',
    PEACH = '#ffcc80',
    ORANGE = '#ff9800',
    CLEMENTINE = '#ef6c00',
    AMARELO = '#fff59d',
    YELLOW = '#ffeb3b',
    SUNFLOWER = '#fbc02d',
    LILAC = '#ce93d8',
    PURPLE = '#9c27b0',
    REGAL = '#6a1b9a',
    BLUSH = '#f48fb1',
    PINK = '#e91e63',
    FUCHSIA = '#ad1457',
    CARIBBEAN = '#80cbc4',
    TEAL = '#009688',
    PEACOCK = '#00695c',
    OXFORD_BLUE = '#00001e'
}

export const colorCode = {
    '10': TileBackgroundColors.BLACK,
    '20': TileBackgroundColors.WHITE,
    '23': TileBackgroundColors.BLANC,
    '33': TileBackgroundColors.SMOKE,
    '30': TileBackgroundColors.GREY,
    '37': TileBackgroundColors.BATTLESHIP,
    '43': TileBackgroundColors.AQUA,
    '40': TileBackgroundColors.BLUE,
    '106': TileBackgroundColors.WATERS,
    '107': TileBackgroundColors.SKY,
    '108': TileBackgroundColors.AKIXI,
    '47': TileBackgroundColors.MIDNIGHT,
    '73': TileBackgroundColors.PISTACHIO,
    '70': TileBackgroundColors.GREEN,
    '77': TileBackgroundColors.FOREST,
    '63': TileBackgroundColors.SALMON,
    '60': TileBackgroundColors.RED,
    '67': TileBackgroundColors.ROSITA,
    '116': TileBackgroundColors.PEACH,
    '117': TileBackgroundColors.ORANGE,
    '118': TileBackgroundColors.CLEMENTINE,
    '53': TileBackgroundColors.AMARELO,
    '50': TileBackgroundColors.YELLOW,
    '57': TileBackgroundColors.SUNFLOWER,
    '122': TileBackgroundColors.LILAC,
    '123': TileBackgroundColors.PURPLE,
    '124': TileBackgroundColors.REGAL,
    '125': TileBackgroundColors.BLUSH,
    '126': TileBackgroundColors.PINK,
    '127': TileBackgroundColors.FUCHSIA,
    '128': TileBackgroundColors.CARIBBEAN,
    '129': TileBackgroundColors.TEAL,
    '130': TileBackgroundColors.PEACOCK,
    '131': TileBackgroundColors.OXFORD_BLUE,
    '132': TileBackgroundColors.ELECTRIC_INDIGO,
    '133': TileBackgroundColors.AQUAMARINE,
    '134': TileBackgroundColors.VIVID_SKY
}

const setTitleSize = (title: string): string => {
    let viewBoxSettings = '0 0 200 15'

    if (title.length > 20) {
        viewBoxSettings = '0 0 250 15'
    }
    return viewBoxSettings
}

const setValueSize = (value: string): string => {
    let viewBoxSettings = '0 0 30 15'

    if (value.length > 2) {
        viewBoxSettings = `0 0 ${value.length * 10} 15`
    }

    return viewBoxSettings
}

const TilePreview: React.FC<TilePreviewProps> = ({
    backgroundColour,
    textColour,
    tileDisplayedAs,
    isGraphDialAxisReversed,
    isGraphDialFacadeReversed,
    graphDialScaleRangeMax,
    displayTitle,
    title,
    statID,
    currentValueNormalised,
    currentValueText,
    imageURL,
    fitImage,
    unitType
}): ReactElement => {
    const className = `tile`
    const [tileSize, setTileSize] = useState(0)
    const canvasRef = useRef<HTMLDivElement>(null)

    const speedometers = [1000, 1010, 1050, 1100, 1150, 1200]
    const isImageType = (tileDisplayedAs as number) === 5000

    let backgroundColor = ''
    let textColor = ''

    if (hasKey(colorCode, backgroundColour)) {
        backgroundColor = colorCode[backgroundColour]
    }

    if (hasKey(colorCode, textColour)) {
        textColor = colorCode[textColour]
    }

    useEffect(() => {
        const tile = document.querySelector('.tile')

        if (tile) {
            let width = tile.getBoundingClientRect().width / 1.5

            if (width > 150) {
                width = 150
            }

            setTileSize(width)
        }
    })

    useEffect(() => {
        if (tileSize > 0) {
            let dialControl = null
            const dialType = tileDisplayedAs
            if (canvasRef.current) {
                const canvasElement = canvasRef.current

                const children = canvasElement.children
                children[0]?.remove()

                switch (dialType) {
                    case TileDisplayedAsGraphDialValues.TILE_DISPLAYED_AS_GRAPH_DIAL_GRADATED_DIAL:
                    case TileDisplayedAsGraphDialValues.TILE_DISPLAYED_AS_GRAPH_DIAL_GRADATED_DIAL_2:
                        dialControl = new CGradatedDial(canvasElement, dialType)
                        break
                    case TileDisplayedAsGraphDialValues.TILE_DISPLAYED_AS_GRAPH_DIAL_GLIDE_SLOPE:
                    case TileDisplayedAsGraphDialValues.TILE_DISPLAYED_AS_GRAPH_DIAL_GLIDE_SLOPE_2:
                        dialControl = new CGlideSlope(canvasElement, dialType)
                        break
                    case TileDisplayedAsGraphDialValues.TILE_DISPLAYED_AS_GRAPH_DIAL_BULLSEYE:
                        dialControl = new CBullseye(canvasElement, dialType)
                        break
                    case TileDisplayedAsGraphDialValues.TILE_DISPLAYED_AS_GRAPH_DIAL_BARGAUGE:
                        dialControl = new CBarGauge(canvasElement, dialType)
                        break
                    case TileDisplayedAsGraphDialValues.TILE_DISPLAYED_AS_GRAPH_DIAL_CONEGAUGE:
                        dialControl = new CConeGauge(canvasElement, dialType)
                        break
                    case TileDisplayedAsGraphDialValues.TILE_DISPLAYED_AS_GRAPH_DIAL_PROGRESS_RING:
                    case TileDisplayedAsGraphDialValues.TILE_DISPLAYED_AS_GRAPH_DIAL_PROGRESS_RING_2:
                    default:
                        dialControl = new CProgressRing(canvasElement, dialType)
                        break
                }
                dialControl.setReverseGaugeFace(isGraphDialFacadeReversed)
                dialControl.setReverseGaugeDirection(isGraphDialAxisReversed)
                dialControl.setBackgroundColour(backgroundColor)
                dialControl.setForegroundColour(textColor)
                dialControl.setDisplayValue(currentValueText)
                dialControl.setSize(tileSize, tileSize)
                if (dialType === TileDisplayedAsGraphDialValues.TILE_DISPLAYED_AS_GRAPH_DIAL_CONEGAUGE) {
                    // Cone gauge only works with values between 0 and 1.
                    dialControl.setValue(currentValueNormalised / graphDialScaleRangeMax)
                } else {
                    dialControl.setValue(currentValueNormalised)
                }
                dialControl.setMinimumValue(0)
                dialControl.setMaximumValue(graphDialScaleRangeMax)
            }
        }
    }, [
        tileSize,
        backgroundColour,
        textColour,
        tileDisplayedAs,
        isGraphDialAxisReversed,
        isGraphDialFacadeReversed,
        graphDialScaleRangeMax,
        title,
        statID,
        currentValueNormalised,
        currentValueText
    ])

    const canvasWrapperClass =
        speedometers.indexOf(tileDisplayedAs) === -1 && !isImageType && tileDisplayedAs !== -1
            ? 'canvas-wrapper'
            : 'canvas-wrapper-hidden'

    const imageTileClass = `image-tile ${fitImage ? 'contain' : 'cover'}`

    return (
        <>
            <div className={className} style={{ backgroundColor, color: textColor }}>
                {isImageType ? (
                    <div className={imageTileClass} style={{ backgroundImage: `url('${imageURL}')` }} />
                ) : (
                    <React.Fragment>
                        {displayTitle && (
                            <div className='tile-title-wrapper'>
                                <svg viewBox={setTitleSize(title)} className='tile-title'>
                                    <text
                                        x='50%'
                                        y='50%'
                                        dominantBaseline='middle'
                                        textAnchor='middle'
                                        fill={textColor}
                                    >
                                        {title}
                                    </text>
                                </svg>
                            </div>
                        )}
                        <div
                            className='tile-value-wrapper' /* style={{width: `${tileSize}px`, height: `${tileSize}px`}} */
                        >
                            {/* Speedometer */}
                            {speedometers.indexOf(tileDisplayedAs) > -1 && (
                                <DashboardSpeedometerBlack
                                    min={0}
                                    max={graphDialScaleRangeMax}
                                    value={currentValueNormalised}
                                    tileType={tileDisplayedAs}
                                    isGraphDialAxisReversed={isGraphDialAxisReversed}
                                    isGraphDialFacadeReversed={isGraphDialFacadeReversed}
                                    unitType={unitType}
                                />
                            )}

                            {/* Simple text */}
                            {tileDisplayedAs === -1 && (
                                <svg viewBox={setValueSize(currentValueText)} className='tile-value'>
                                    <text
                                        x='50%'
                                        y='50%'
                                        dominantBaseline='middle'
                                        textAnchor='middle'
                                        fill={textColor}
                                    >
                                        {currentValueText}
                                    </text>
                                </svg>
                            )}

                            {/* Canvas */}
                            <div
                                id={`canvas-wrapper-${statID}`}
                                className={canvasWrapperClass}
                                style={{ backgroundColor, color: 'black' }}
                            >
                                <div ref={canvasRef}></div>
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </>
    )
}

export default TilePreview
